import { processingRealtimeStore } from '../services/processingRealtimeStore';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Coins,
  Database,
  ExternalLink,
  Filter,
  Play,
  RefreshCw,
  Workflow,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  apiService,
  CostEstimate,
  ExecutePromptRequest,
  getApiErrorMessage,
  Index,
  ProcessingStreamEvent,
  PromptRun,
  PromptRunFailedSegmentsManifest,
} from '../services/api';
import { usePassiveApiActivity } from '../hooks/usePassiveApiActivity';
import { isPromptRunInProgress } from '../utils/realtimeStreamEligibility';
import { useEngineCatalog } from '../hooks/useEngineCatalog';
import { useAuth } from '../contexts/AuthContext';
import { useTabActivity } from '../contexts/TabActivityContext';
import { commitOwnedSearch } from '../navigation/historyState';
import type { ExecuteSelection } from '../navigation/executeNavigation';
import { formatCredits } from '../utils/credits';
import { IndexSortField, sortIndexes } from '../utils/indexSort';
import {
  describeInsufficientCredits,
  extractInsufficientCredits,
  isBlockingPromptRunEstimateError,
} from '../utils/billingErrors';
import { toPromptRunFromProcessingEvent } from '../utils/promptRunEventMapper';
import { preserveScrollPosition } from '../utils/preserveScrollPosition';
import {
  getPromptRunFallbackSummary,
  getPromptRunIndexId,
  sortPromptRunsByExecutionDesc,
} from '../utils/promptRunProgress';
import {
  isPromptRunTerminalStatus,
} from '../utils/promptRunFailures';
import { isRetryableStreamRequestError } from '../services/idempotency';
import {
  clearPendingIdempotencyAttempt,
  getOrCreatePendingIdempotencyAttempt,
  PendingIdempotencyAttempt,
} from '../services/pendingIdempotencyAttempt';
import {
  getEffectivePromptExecutionConfig,
  hasPromptSelectionFilter,
} from '../features/workflows/config';
import { formatEngineFieldLabel, getEngineSegmentationLabel } from '../features/workflows/presentation';
import { extractConditionFieldsFromSchemas } from '../utils/conditionSearchFields';
import { getPromptSchemaDisplayFields } from './prompt-schema/schemaFields';
import IndexGallery from './IndexGallery';
import WorkflowVideoSelection from './WorkflowVideoSelection';
import WorkflowSortControls, { WorkflowSortField } from './WorkflowSortControls';
import EngineGallery from './EngineGallery';
import WorkflowFilterEditor from './WorkflowFilterEditor';
import WorkflowRunResults from './WorkflowRunResults';
import PromptRunProgressCardCompact from './PromptRunProgressCardCompact';
import ProcessingConnectionNotice from './ProcessingConnectionNotice';
import './WorkflowsTab.css';

const OWNED_KEYS = ['index', 'prompt', 'run'];
const PAGE_SIZE = 50;
const INDEX_SORT_FIELDS: readonly WorkflowSortField<IndexSortField>[] = [
  { field: 'date', label: 'Created', defaultAscending: false },
  { field: 'name', label: 'Name', defaultAscending: true },
];
const WORKFLOW_RUN_ATTEMPT_NAMESPACE = 'workflow-run';

const upsertRun = (runs: PromptRun[], incoming: PromptRun): PromptRun[] => {
  const byId = new Map(runs.map((run) => [run.run_id, run]));
  const current = byId.get(incoming.run_id);
  byId.set(incoming.run_id, {
    ...current,
    ...incoming,
    run_context: incoming.run_context || current?.run_context || {},
  });
  return sortPromptRunsByExecutionDesc(Array.from(byId.values()));
};

const WorkflowsTab: React.FC<{ isActive?: boolean }> = ({ isActive: requestedIsActive = true }) => {
  const { isActive: isTabActive } = useTabActivity();
  const isActive = requestedIsActive && isTabActive;
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const {
    prompts,
    isLoading: isLoadingPrompts,
    error: promptCatalogError,
    refresh: refreshPrompts,
  } = useEngineCatalog();
  const [indexes, setIndexes] = useState<Index[] | null>(null);
  const [indexSortBy, setIndexSortBy] = useState<IndexSortField>('date');
  const [indexSortAsc, setIndexSortAsc] = useState(false);
  const sortedIndexes = useMemo(
    () => sortIndexes(indexes || [], indexSortBy, indexSortAsc),
    [indexes, indexSortBy, indexSortAsc],
  );
  const routeParams = new URLSearchParams(location.search);
  const selectedIndexId = routeParams.get('index') || '';
  const selectedPromptId = routeParams.get('prompt') || '';
  const selectedRunId = routeParams.get('run') || '';
  const [videoSelection, setVideoSelection] = useState<{
    byIndex: Record<string, string[] | null>;
    handoffKeys: Record<string, string>;
  }>({ byIndex: {}, handoffKeys: {} });
  const handoff = (location.state as { executeSelection?: ExecuteSelection } | null)?.executeSelection;
  const pendingHandoff = handoff?.indexId === selectedIndexId
    && videoSelection.handoffKeys[selectedIndexId] !== location.key ? handoff : null;
  // Read transfers before default engine selection normalizes the URL and drops
  // history state. Retain each index's selection with the mounted workspace.
  const selectedVideoIds = pendingHandoff
    ? pendingHandoff.videoIds
    : videoSelection.byIndex[selectedIndexId] ?? null;
  const selectionKey = pendingHandoff ? location.key : videoSelection.handoffKeys[selectedIndexId];
  useEffect(() => {
    if (!isActive || !pendingHandoff) return;
    setVideoSelection((current) => ({
      byIndex: { ...current.byIndex, [pendingHandoff.indexId]: pendingHandoff.videoIds },
      handoffKeys: { ...current.handoffKeys, [pendingHandoff.indexId]: location.key },
    }));
  }, [isActive, location.key, pendingHandoff]);
  const changeVideoSelection = useCallback((videoIds: string[] | null) => {
    setVideoSelection((current) => ({
      ...current,
      byIndex: { ...current.byIndex, [selectedIndexId]: videoIds },
    }));
  }, [selectedIndexId]);
  const executionTarget = useMemo<ExecutePromptRequest['target']>(() => selectedVideoIds === null
    ? { type: 'index', index_id: selectedIndexId }
    : { type: 'videos', index_id: selectedIndexId, video_ids: Array.from(new Set(selectedVideoIds)).sort() },
  [selectedIndexId, selectedVideoIds]);
  const targetKey = JSON.stringify(executionTarget);
  const [validatedTargetKey, setValidatedTargetKey] = useState<string | null>(null);
  const onSelectionValidityChange = useCallback((valid: boolean) => {
    setValidatedTargetKey(valid ? targetKey : null);
  }, [targetKey]);
  const isSelectionValid = selectedVideoIds === null
    || (selectedVideoIds.length > 0 && validatedTargetKey === targetKey);
  const [runs, setRuns] = useState<PromptRun[]>([]);
  const canObserve = usePassiveApiActivity(runs.some(isPromptRunInProgress), isActive);
  const runsRef = useRef(runs);
  runsRef.current = runs;
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoadingIndexes, setIsLoadingIndexes] = useState(true);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [isLoadingMoreRuns, setIsLoadingMoreRuns] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [isEstimating, setIsEstimating] = useState(false);
  const [estimate, setEstimate] = useState<CostEstimate | null>(null);
  const [estimateRevision, setEstimateRevision] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [costError, setCostError] = useState<string | null>(null);
  const [costErrorBlocksRun, setCostErrorBlocksRun] = useState(false);
  const [costRecoveryPath, setCostRecoveryPath] = useState<string | null>(null);
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(() => new Set(selectedRunId ? [selectedRunId] : []));
  const expandedIndexRef = useRef(selectedIndexId);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const collapseSpaceRef = useRef<HTMLDivElement>(null);
  const collapseScrollTopRef = useRef<number | null>(null);
  const [manifests, setManifests] = useState<Record<string, PromptRunFailedSegmentsManifest>>({});
  const estimateRequestRef = useRef(0);
  const runsRequestRef = useRef(0);
  const launchInFlightRef = useRef(false);
  const selectedIndexIdRef = useRef(selectedIndexId);
  selectedIndexIdRef.current = selectedIndexId;

  const selectedIndex = indexes?.find((index) => index.index_id === selectedIndexId) || null;
  const selectedIndexAvailable = Boolean(selectedIndex);
  const selectedIndexIsEmpty = selectedVideoIds === null && selectedIndex?.video_count === 0;
  const selectedPrompt = prompts.find((prompt) => prompt.prompt_id === selectedPromptId) || null;
  const selectedRun = runs.find((run) => run.run_id === selectedRunId) || null;
  const selectedRunAvailable = Boolean(selectedRun);
  const selectedConfig = selectedPrompt ? getEffectivePromptExecutionConfig(selectedPrompt, null) : null;
  const selectedHasFilter = hasPromptSelectionFilter(selectedPrompt?.selection_filter);
  const selectedFieldGroups = selectedPrompt ? [
    { label: 'Segments', fields: getPromptSchemaDisplayFields(selectedPrompt.json_schema) },
    { label: 'Whole video', fields: getPromptSchemaDisplayFields(selectedPrompt.video_level?.json_schema) },
  ].filter((group) => group.fields.length > 0) : [];

  const updateUrl = useCallback((updates: { index?: string | null; prompt?: string | null; run?: string | null }, mode: 'push' | 'replace' = 'push') => {
    if (!isActiveRef.current) return;
    commitOwnedSearch({
      location,
      navigate,
      ownedKeys: OWNED_KEYS,
      updates: {
        index: updates.index === undefined ? selectedIndexId || null : updates.index,
        prompt: updates.prompt === undefined ? selectedPromptId || null : updates.prompt,
        run: updates.run === undefined ? selectedRunId || null : updates.run,
      },
      pathname: '/workflows',
      mode,
    });
  }, [location, navigate, selectedIndexId, selectedPromptId, selectedRunId]);

  const loadIndexes = useCallback(async () => {
    try {
      const loadedIndexes = await apiService.getIndexes({ includePreview: true });
      setIndexes(loadedIndexes.filter((index) => !index.is_default && index.index_id !== 'playground'));
    } catch (loadError) {
      setError(getApiErrorMessage(loadError, 'Indexes could not be refreshed. Use Refresh to try again.'));
    } finally {
      setIsLoadingIndexes(false);
    }
  }, []);

  useEffect(() => {
    // Retain the rendered workspace, but observe media added/deleted elsewhere.
    if (isActive) void loadIndexes();
  }, [isActive, loadIndexes]);

  useEffect(() => {
    if (!isActive) return;
    // Normalize both selections in one URL update. Separate effects previously
    // overwrote each other's defaults with stale local selection state.
    const updates: { index?: string | null; prompt?: string | null; run?: string | null } = {};
    // An explicit deep link may refer to an index created in another tab;
    // never replace it using the retained (not yet refreshed) catalog.
    if (!selectedIndexId && sortedIndexes.length) {
      updates.index = sortedIndexes[0].index_id;
      updates.run = null;
    }
    if (!isLoadingPrompts && !promptCatalogError && !prompts.some((prompt) => prompt.prompt_id === selectedPromptId)) {
      const nextPromptId = prompts[0]?.prompt_id || '';
      if (nextPromptId !== selectedPromptId) updates.prompt = nextPromptId || null;
    }
    if (Object.keys(updates).length > 0) updateUrl(updates, 'replace');
  }, [isActive, sortedIndexes, isLoadingPrompts, promptCatalogError, prompts, selectedIndexId, selectedPromptId, updateUrl]);

  const loadRuns = useCallback(async (
    append: boolean = false,
    cursor: string | null = null,
    reconcile: boolean = false,
  ) => {
    const requestedIndexId = selectedIndexId;
    const requestId = ++runsRequestRef.current;
    const observed = new Map(runsRef.current.map((run) => [run.run_id, run]));
    if (!requestedIndexId) {
      setRuns([]);
      setNextCursor(null);
      return;
    }
    if (append) {
      setIsLoadingMoreRuns(true);
    } else if (!reconcile) {
      setIsLoadingRuns(true);
      setIsLoadingMoreRuns(false);
    }
    setError(null);
    try {
      const page = await apiService.getPromptRunsByIndexPage(
        requestedIndexId,
        PAGE_SIZE,
        append ? cursor || undefined : undefined,
      );
      if (runsRequestRef.current !== requestId || selectedIndexIdRef.current !== requestedIndexId) return;
      setRuns((current) => {
        const pageIds = new Set(page.data.map((run) => run.run_id));
        const base = append || reconcile ? current : current.filter((run) => pageIds.has(run.run_id));
        return page.data.reduce((result, run) => {
          const live = current.find((candidate) => candidate.run_id === run.run_id);
          // Preserve events received while this snapshot was in flight.
          const incoming = live && observed.get(run.run_id) !== live
            ? { ...run, ...live, run_context: { ...run.run_context, ...live.run_context } }
            : run;
          return upsertRun(result, incoming);
        }, base);
      });
      if (!reconcile) setNextCursor(page.pagination?.has_more ? page.pagination.next_cursor || null : null);
    } catch (loadError) {
      if (runsRequestRef.current === requestId && selectedIndexIdRef.current === requestedIndexId) {
        setError('Execution results could not be loaded for this index. Choose another index or use Refresh to try again.');
      }
    } finally {
      if (runsRequestRef.current === requestId) {
        append ? setIsLoadingMoreRuns(false) : setIsLoadingRuns(false);
      }
    }
  }, [selectedIndexId]);

  useEffect(() => {
    if (isLoadingIndexes) return;
    setRuns([]);
    setNextCursor(null);
    setManifests({});
    if (selectedIndexAvailable) void loadRuns(false);
  }, [isLoadingIndexes, loadRuns, selectedIndexAvailable]);

  useEffect(() => {
    const indexChanged = expandedIndexRef.current !== selectedIndexId;
    expandedIndexRef.current = selectedIndexId;
    setExpandedRuns((current) => {
      if (!indexChanged && (!selectedRunId || current.has(selectedRunId))) return current;
      const next = indexChanged ? new Set<string>() : new Set(current);
      if (selectedRunId) next.add(selectedRunId);
      return next;
    });
    if (indexChanged && collapseSpaceRef.current) collapseSpaceRef.current.style.height = '0px';
  }, [selectedIndexId, selectedRunId]);

  const preserveResultScroll = useCallback((scrollTop: number) => {
    const workspace = workspaceRef.current;
    const space = collapseSpaceRef.current;
    if (!workspace || !space) return;
    preserveScrollPosition(workspace, space, scrollTop);
  }, []);

  useLayoutEffect(() => {
    if (!isActive) return;
    preserveResultScroll(collapseScrollTopRef.current ?? workspaceRef.current?.scrollTop ?? 0);
    collapseScrollTopRef.current = null;
  }, [expandedRuns, isActive, preserveResultScroll]);

  useEffect(() => {
    if (!isActive || !selectedIndexAvailable || !selectedRunId || isLoadingIndexes || isLoadingRuns || selectedRunAvailable) return;
    let cancelled = false;
    void apiService.getPromptRun(selectedRunId)
      .then((run) => {
        if (cancelled || selectedIndexIdRef.current !== selectedIndexId) return;
        if (getPromptRunIndexId(run) !== selectedIndexId) {
          updateUrl({ run: null }, 'replace');
          return;
        }
        setRuns((current) => upsertRun(current, run));
      processingRealtimeStore.refresh();
      })
      .catch((loadError) => {
        if (cancelled) return;
        if (loadError?.response?.status === 404 || loadError?.response?.status === 403) {
          setError('This execution is no longer available. Choose another execution below.');
          updateUrl({ run: null }, 'replace');
        } else {
          setError('This execution could not be loaded. Refresh to try again.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isActive, isLoadingIndexes, isLoadingRuns, selectedIndexAvailable, selectedRunAvailable, selectedIndexId, selectedRunId, updateUrl]);

  useEffect(() => {
    const requestId = ++estimateRequestRef.current;
    if (!isActive) return;
    if (!selectedIndex || !selectedPrompt || selectedIndexIsEmpty || !isSelectionValid) {
      setIsEstimating(false);
      setEstimate(null);
      setCostError(null);
      setCostErrorBlocksRun(false);
      setCostRecoveryPath(null);
      return;
    }
    setIsEstimating(true);
    setEstimate(null);
    setCostError(null);
    setCostErrorBlocksRun(false);
    setCostRecoveryPath(null);
    const timer = window.setTimeout(() => {
      void apiService.estimatePromptRunCost({
        prompt_id: selectedPrompt.prompt_id,
        target: executionTarget,
      })
        .then((nextEstimate) => {
          if (estimateRequestRef.current === requestId) setEstimate(nextEstimate);
        })
        .catch((estimateError) => {
          if (estimateRequestRef.current !== requestId) return;
          const insufficient = extractInsufficientCredits(estimateError);
          const unavailableIndex = estimateError?.response?.status === 404;
          setCostError(unavailableIndex ? 'This index is unavailable. Choose another index or add media in Index.' : insufficient
            ? describeInsufficientCredits(insufficient)
            : getApiErrorMessage(estimateError, 'Cost estimate is unavailable.'));
          setCostErrorBlocksRun(unavailableIndex || Boolean(insufficient) || isBlockingPromptRunEstimateError(estimateError));
          setCostRecoveryPath(insufficient?.purchasePath || null);
        })
        .finally(() => {
          if (estimateRequestRef.current === requestId) setIsEstimating(false);
        });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [isActive, selectedIndex, selectedIndexIsEmpty, selectedPrompt, executionTarget, isSelectionValid, estimateRevision]);

  const progressRunIdsKey = runs.map((run) => run.run_id).slice(0, 100).join(',');
  useEffect(() => {
    if (!canObserve || !selectedIndexAvailable) return;
    return processingRealtimeStore.subscribe((event: ProcessingStreamEvent) => {
      if (!event.event_type.startsWith('run.')) return;
      const runId = String(event.payload?.run_id || event.run_id || '').trim();
      if (!runId || (event.index_id && event.index_id !== selectedIndexId)) return;
      setRuns((current) => {
        const existing = current.find((run) => run.run_id === runId);
        const next = toPromptRunFromProcessingEvent(event, existing);
        return next ? upsertRun(current, next) : current;
      });
      if (isPromptRunTerminalStatus(event.payload?.status)) setEstimateRevision((value) => value + 1);
    }, { index_id: selectedIndexId, run_ids: progressRunIdsKey.split(',').filter(Boolean) });
  }, [canObserve, progressRunIdsKey, selectedIndexAvailable, selectedIndexId]);

  const launchWorkflow = async () => {
    if (!selectedPrompt || !selectedIndexAvailable || selectedIndexIsEmpty || !isSelectionValid || launchInFlightRef.current) return;
    const userId = String(currentUser?.uid || '').trim();
    if (!userId) {
      setError('Your authenticated session is not available. Sign in and try again.');
      return;
    }
    const request = {
      prompt_id: selectedPrompt.prompt_id,
      target: executionTarget,
    };
    const namespace = `${WORKFLOW_RUN_ATTEMPT_NAMESPACE}:${userId}`;
    let attempt: PendingIdempotencyAttempt | null = null;
    launchInFlightRef.current = true;
    setIsLaunching(true);
    setError(null);
    try {
      attempt = await getOrCreatePendingIdempotencyAttempt({
        namespace,
        operation: 'execute-prompt-run',
        requestPayload: request,
        keyPrefix: 'prompt-run-execute',
      });
      const run = await apiService.executePromptRun(request, attempt.idempotencyKey);
      clearPendingIdempotencyAttempt({
        namespace,
        operation: attempt.operation,
        requestFingerprint: attempt.requestFingerprint,
        expectedIdempotencyKey: attempt.idempotencyKey,
      });
      if (selectedIndexIdRef.current !== selectedIndexId) return;
      setRuns((current) => upsertRun(current, run));
      processingRealtimeStore.refresh();
      setExpandedRuns((current) => new Set(current).add(run.run_id));
      updateUrl({ run: run.run_id });
    } catch (launchError) {
      if (attempt && !isRetryableStreamRequestError(launchError)) {
        clearPendingIdempotencyAttempt({
          namespace,
          operation: attempt.operation,
          requestFingerprint: attempt.requestFingerprint,
          expectedIdempotencyKey: attempt.idempotencyKey,
        });
      }
      const insufficient = extractInsufficientCredits(launchError);
      setError(insufficient
        ? describeInsufficientCredits(insufficient)
        : getApiErrorMessage(launchError, 'Failed to start workflow.'));
      setCostRecoveryPath(insufficient?.purchasePath || null);
    } finally {
      launchInFlightRef.current = false;
      setIsLaunching(false);
    }
  };

  const loadManifest = useCallback(async (runId: string): Promise<PromptRunFailedSegmentsManifest | null> => {
    const requestedIndexId = selectedIndexIdRef.current;
    try {
      const manifest = await apiService.getPromptRunFailedSegments(runId);
      if (selectedIndexIdRef.current === requestedIndexId) {
        setManifests((current) => ({ ...current, [runId]: manifest }));
      }
      return manifest;
    } catch (manifestError) {
      if (selectedIndexIdRef.current === requestedIndexId) {
        setError(getApiErrorMessage(manifestError, 'Failed segments could not be loaded.'));
      }
      return null;
    }
  }, []);

  const selectedRunIsTerminal = Boolean(selectedRun && isPromptRunTerminalStatus(selectedRun.status));
  useEffect(() => {
    if (selectedRunIsTerminal) {
      void loadManifest(selectedRunId);
    }
  }, [loadManifest, selectedRunId, selectedRunIsTerminal]);

  const toggleExpanded = (runId: string) => {
    const isClosing = expandedRuns.has(runId);
    if (isClosing) collapseScrollTopRef.current = workspaceRef.current?.scrollTop ?? null;
    setExpandedRuns((current) => {
      const next = new Set(current);
      if (isClosing) next.delete(runId);
      else next.add(runId);
      return next;
    });
    if (!isClosing) updateUrl({ run: runId });
    else if (selectedRunId === runId) updateUrl({ run: null }, 'replace');
  };

  return (
    <div className="workflows-tab" ref={workspaceRef} onScroll={(event) => {
      if (isActive && collapseSpaceRef.current?.offsetHeight) preserveResultScroll(event.currentTarget.scrollTop);
    }}>
      <header className="workflows-header">
        <div className="workflows-title">
          <Workflow size={22} aria-hidden="true" />
          <div>
            <h2>Execute</h2>
            <p>Choose your media, run an engine, and explore the results.</p>
          </div>
        </div>
        <button
          type="button"
          className="workflows-refresh"
          onClick={() => {
            void loadIndexes();
            void refreshPrompts();
            void loadRuns(false);
            setEstimateRevision((revision) => revision + 1);
          }}
          disabled={isLoadingRuns || isLoadingMoreRuns || isLoadingPrompts}
        >
          <RefreshCw size={15} className={(isLoadingRuns || isLoadingMoreRuns || isLoadingPrompts) ? 'workflows-spinning' : ''} />
          Refresh
        </button>
      </header>

      {error && (
        <div className="workflows-error" role="alert">
          <AlertCircle size={15} /> <span>{error}</span>
          <button type="button" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <section className="workflow-launch-panel" aria-labelledby="workflow-launch-title">
        <div className="workflow-launch-heading">
          <div>
            <h3 id="workflow-launch-title">New execution</h3>
            <p>Your engine extracts the fields you need, then selects matching content.</p>
          </div>
          <div className="workflow-launch-flow" aria-hidden="true">
            <Database size={15} /><ArrowRight size={13} /><Workflow size={15} /><ArrowRight size={13} /><CheckCircle2 size={15} />
          </div>
        </div>

        <div className="workflow-setup-step workflow-index-step">
          <div className="workflow-step-heading">
            <span className="workflow-step-number">1</span>
            <div><h4>Choose an index</h4></div>
          </div>
          <div className="workflow-index-control">
            <div className="workflow-index-sort">
              <WorkflowSortControls
                fields={INDEX_SORT_FIELDS}
                value={indexSortBy}
                ascending={indexSortAsc}
                onChange={(field, ascending) => {
                  setIndexSortBy(field);
                  setIndexSortAsc(ascending);
                }}
                ariaLabel="Sort indexes"
              />
            </div>
            <IndexGallery
              indexes={sortedIndexes}
              selectedIndexId={selectedIndexId}
              isLoading={isLoadingIndexes}
              onSelect={(indexId) => {
                updateUrl({ index: indexId, run: null });
              }}
              disabled={isLoadingIndexes || isLaunching || !indexes?.length}
            />
            {selectedIndexAvailable && (
              <WorkflowVideoSelection
                indexId={selectedIndexId}
                indexName={selectedIndex?.name || ''}
                totalVideoCount={selectedIndex?.video_count}
                runCatalog={runs}
                selectedVideoIds={selectedVideoIds}
                onChange={changeVideoSelection}
                onValidityChange={onSelectionValidityChange}
                selectionKey={selectionKey}
                isActive={isActive}
                disabled={isLaunching}
              />
            )}
            {indexes?.length === 0 && (
              <p className="workflow-step-help">No indexes yet. <button type="button" onClick={() => navigate('/index')}>Add media in Index</button> to get started.</p>
            )}
            {indexes && selectedIndexId && !selectedIndexAvailable && (
              <p className="workflow-step-help">This index isn't in the loaded list. Choose an index above or <button type="button" onClick={() => void loadIndexes()}>Refresh indexes</button>.</p>
            )}
          </div>
        </div>

          <div className="workflow-setup-step workflow-engine-picker">
            <div className="workflow-step-heading">
              <span className="workflow-step-number">2</span>
              <div><h4>Choose an engine</h4><p>Processing settings, extraction fields, and optional filters are saved in each engine.</p></div>
            </div>
            <EngineGallery selectedPromptId={selectedPromptId} isActive={isActive} disabled={isLaunching} onSelect={(prompt) => {
              updateUrl({ prompt: prompt.prompt_id });
            }} />
          </div>

        {selectedIndexIsEmpty && (
          <div className="workflows-empty">
            This index has no media yet.{' '}
            <button type="button" onClick={() => navigate(`/index?${new URLSearchParams({ index: selectedIndexId })}`)}>Add media in Index</button>
            {' '}to run an engine.
          </div>
        )}
        {selectedIndex && !selectedIndexIsEmpty && selectedPrompt && selectedConfig && (
          <div className="workflow-setup-step workflow-review-step">
            <div className="workflow-step-heading">
              <span className="workflow-step-number">3</span>
              <div><h4>Review and run</h4><p>Confirm the media, extracted information, and results this workflow will produce.</p></div>
            </div>
            <div className="workflow-run-review">
              <ol className="workflow-review-flow" aria-label="Selected workflow stages">
                <li className="workflow-review-stage">
                  <span className="workflow-review-icon" aria-hidden="true"><Database size={18} /></span>
                  <div className="workflow-review-stage-content">
                    <span className="workflow-review-label">Source index</span>
                    <h5>{selectedIndex.name}</h5>
                    <span className="workflow-review-media-count">{selectedVideoIds !== null
                      ? `${selectedVideoIds.length.toLocaleString()} videos selected`
                      : selectedIndex.video_count === undefined ? 'All media selected' : `${selectedIndex.video_count.toLocaleString()} media selected`}</span>
                  </div>
                </li>
                <li className="workflow-review-stage">
                  <span className="workflow-review-icon" aria-hidden="true"><Workflow size={18} /></span>
                  <div className="workflow-review-stage-content">
                    <div className="workflow-review-stage-heading">
                      <span className="workflow-review-label">Extract with engine</span>
                      <button type="button" onClick={() => {
                        const params = new URLSearchParams(location.search);
                        params.set('edit', '1');
                        navigate({ pathname: '/workflows', search: params.toString() });
                      }}>Show details <ExternalLink size={12} /></button>
                    </div>
                    <h5>{selectedPrompt.name}</h5>
                    <div className="workflow-config-chips">
                      <span title="Video segmentation">{getEngineSegmentationLabel(selectedConfig)}</span>
                      <span>Visual search: {selectedConfig.enable_image_embedding ? 'On' : 'Off'}</span>
                    </div>
                    <div className="workflow-review-fields">
                      {selectedFieldGroups.map((group) => (
                        <div key={group.label}>
                          <span className="workflow-review-field-level">{group.label}</span>
                          {group.fields.slice(0, 4).map((field) => (
                            <span className="workflow-review-field" key={field.name} title={field.description || field.name}>{formatEngineFieldLabel(field.name)}</span>
                          ))}
                          {group.fields.length > 4 && <span className="workflow-review-field-more" title={group.fields.slice(4).map((field) => formatEngineFieldLabel(field.name)).join(', ')}>+{group.fields.length - 4} fields</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                </li>
                <li className="workflow-review-stage">
                  <span className="workflow-review-icon" aria-hidden="true"><Filter size={18} /></span>
                  <div className="workflow-review-stage-content">
                    <span className="workflow-review-label">Select results</span>
                    <h5>{selectedHasFilter ? 'Apply saved filters' : 'Keep all extracted results'}</h5>
                    {selectedHasFilter ? (
                      <div className="workflow-review-filter">
                        <WorkflowFilterEditor
                          value={selectedPrompt.selection_filter || null}
                          segmentFields={extractConditionFieldsFromSchemas([selectedPrompt.json_schema])}
                          videoFields={extractConditionFieldsFromSchemas([selectedPrompt.video_level?.json_schema])}
                          editable={false}
                          onChange={() => undefined}
                        />
                      </div>
                    ) : <p className="workflow-review-note">No selection filter. Browse all extracted information.</p>}
                  </div>
                </li>
              </ol>
              <aside className="workflow-run-confirmation" aria-label="Usage and launch">
                <div className="workflow-estimate" aria-live="polite" aria-busy={isEstimating}>
                  <span className="workflow-estimate-label"><Coins size={15} aria-hidden="true" /> Estimated usage</span>
                  <div className="workflow-estimate-amount">
                    <strong>{isEstimating ? 'Calculating…' : estimate ? formatCredits(Math.ceil(estimate.estimated_mt), { maximumFractionDigits: 0 }) : 'Unavailable'}</strong>
                    {!isEstimating && estimate && <span>credits</span>}
                  </div>
                  {estimate && !isEstimating && <small>Rounded up to whole credits.</small>}
                  {estimate && <div className="workflow-estimate-balance"><span>Available balance</span><strong>{formatCredits(estimate.current_balance_mt, { maximumFractionDigits: 2 })} credits</strong></div>}
                </div>
                <button
                  type="button"
                  className="workflow-run-button"
                  onClick={() => void launchWorkflow()}
                  disabled={
                    isLaunching
                    || !isSelectionValid
                    || isEstimating
                    || costErrorBlocksRun
                    || (!estimate && !costError)
                    || Boolean(estimate && !estimate.sufficient_balance)
                  }
                >
                  {isLaunching ? <RefreshCw size={16} className="workflows-spinning" /> : <Play size={16} />}
                  {isLaunching ? 'Starting…' : 'Run workflow'}
                </button>
              </aside>
            </div>
          </div>
        )}
        {costError && (
          <div className="workflows-inline-error">
            <AlertCircle size={14} /> <span>{costError}</span>
            {costRecoveryPath && <button type="button" onClick={() => navigate(costRecoveryPath)}>Add credits</button>}
          </div>
        )}
        {estimate && !estimate.sufficient_balance && (
          <div className="workflows-inline-error">
            <AlertCircle size={14} /> <span>Insufficient credits to run this workflow.</span>
            <button type="button" onClick={() => navigate('/billing?section=credits')}>Add credits</button>
          </div>
        )}
      </section>

      <section className="workflow-history" aria-labelledby="workflow-history-title">
        <div className="workflow-history-heading">
          <div>
            <h3 id="workflow-history-title">Execution results</h3>
            <p>{selectedIndex ? `Progress and results for ${selectedIndex.name}` : 'Select an index to view its workflow runs.'}</p>
          </div>
          {selectedIndexAvailable && <ProcessingConnectionNotice />}
        </div>

        {isLoadingRuns ? (
          <div className="workflows-loading"><RefreshCw size={20} className="workflows-spinning" /> Loading executions…</div>
        ) : !selectedIndexAvailable ? (
          <div className="workflows-empty">Select an index to view and run workflows.</div>
        ) : runs.length === 0 ? (
          <div className="workflows-empty">No workflow executions for this index yet.</div>
        ) : (
          <div className="workflow-run-list">
            {runs.map((run) => {
              return (
                <div className={`workflow-run-entry ${selectedRun?.run_id === run.run_id ? 'selected' : ''}`} key={run.run_id}>
                  <PromptRunProgressCardCompact
                    run={run}
                    presentation="workflow"
                    summary={getPromptRunFallbackSummary(run)}
                    failureManifest={manifests[run.run_id] || null}
                    isExpanded={expandedRuns.has(run.run_id)}
                    onToggleExpand={toggleExpanded}
                  >
                    <WorkflowRunResults run={run} indexId={selectedIndexId} isActive={isActive && expandedRuns.has(run.run_id)} />
                  </PromptRunProgressCardCompact>
                </div>
              );
            })}
            {nextCursor && (
              <button
                type="button"
                className="workflow-load-more"
                onClick={() => void loadRuns(true, nextCursor)}
                disabled={isLoadingMoreRuns}
              >
                {isLoadingMoreRuns ? 'Loading…' : 'Load more executions'}
              </button>
            )}
          </div>
        )}
      </section>
      <div ref={collapseSpaceRef} aria-hidden="true" />
    </div>
  );
};

export default WorkflowsTab;
